/**
 * Turn an Angular HttpErrorResponse into a sentence for a toast.
 *
 * The website API uses three body shapes:
 * - BadAPIRequest: `{"success": false, "error": "Missing admin permission"}` or a plain string
 * - HTTPException: `{"detail": "View-as mode is only available to the bot owner"}`
 * - Request validation: `{"detail": [{"loc": [...], "msg": "Value error, ..."}]}`
 */

const LOC_PREFIXES = new Set(['body', 'query', 'path', 'header']);

export function httpErrorMessage(error: unknown, fallback = 'Unknown error.'): string {
  const message = messageFromBody(responseBody(error));
  return message || fallback;
}

function responseBody(error: unknown): unknown {
  if (!error || typeof error !== 'object' || !('error' in error)) {
    return error;
  }
  const raw = (error as { error?: unknown }).error;
  if (typeof raw !== 'string') {
    return raw;
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

function messageFromBody(body: unknown): string | null {
  if (body == null || body === '') {
    return null;
  }
  if (typeof body === 'string') {
    const trimmed = body.trim();
    return trimmed || null;
  }
  if (typeof body === 'number' || typeof body === 'boolean') {
    return String(body);
  }
  if (Array.isArray(body)) {
    return joinMessages(body.map((item) => messageFromBody(item)));
  }
  if (typeof body !== 'object') {
    return null;
  }

  const record = body as Record<string, unknown>;
  if (typeof record['error'] === 'string' && record['error'].trim()) {
    return record['error'].trim();
  }
  if ('detail' in record) {
    const detail = messageFromDetail(record['detail']);
    if (detail) {
      return detail;
    }
  }
  if (record['error'] != null && typeof record['error'] === 'object') {
    const nested = messageFromBody(record['error']);
    if (nested) {
      return nested;
    }
  }
  if (typeof record['message'] === 'string' && record['message'].trim()) {
    return record['message'].trim();
  }
  if (typeof record['msg'] === 'string' && record['msg'].trim()) {
    return validationText(record);
  }
  return null;
}

function messageFromDetail(detail: unknown): string | null {
  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    return trimmed || null;
  }
  if (Array.isArray(detail)) {
    return joinMessages(detail.map((item) => messageFromBody(item)));
  }
  return messageFromBody(detail);
}

function validationText(record: Record<string, unknown>): string | null {
  const msg = typeof record['msg'] === 'string' ? cleanValidatorMessage(record['msg']) : null;
  if (!msg) {
    return null;
  }
  const field = fieldLabel(record['loc']);
  return field ? `${field}: ${msg}` : msg;
}

function fieldLabel(loc: unknown): string | null {
  if (!Array.isArray(loc)) {
    return null;
  }
  const names = loc.filter(
    (part): part is string => typeof part === 'string' && !LOC_PREFIXES.has(part)
  );
  return names.length ? names[names.length - 1] : null;
}

function cleanValidatorMessage(message: string): string {
  return message.replace(/^(Value error|Assertion failed),\s*/i, '').trim();
}

function joinMessages(parts: Array<string | null>): string | null {
  const messages = parts.filter((part): part is string => !!part);
  return messages.length ? messages.join('; ') : null;
}
